import { LoadingDots } from '@advanza/ui'
import {
    AlertCircle,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    RefreshCw02,
    XCircle,
} from '@untitledui/icons'
import clsx from 'clsx'
import { formatEuro } from 'components/billing/debtorNew/format'
import { Card } from 'components/billing/debtorNew/ui'
import { AuditChargesBucket, AuditInvoiceRow, useBillingRunAudit } from 'hooks/billingRunHooks'
import { ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'

const ProviderLink = ({ id, name }: { id: number; name: string | null }) => (
    <Link
        to={`/service/providers/provider/${id}`}
        target="_blank"
        className="text-primary-600 hover:underline">
        {name || `#${id}`}
    </Link>
)

interface AuditSectionProps {
    title: string
    description: string
    count: number
    /** Extra context shown next to the count, e.g. a money amount. */
    countSuffix?: string
    /** 'issue' (default) shows red when count > 0; 'info' shows a neutral badge. */
    tone?: 'issue' | 'info'
    children: ReactNode
}

/** One audit check: green = passes, red/neutral = needs a look, expandable drilldown. */
const AuditSection = ({
    title,
    description,
    count,
    countSuffix,
    tone = 'issue',
    children,
}: AuditSectionProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const passes = count === 0
    const isInfo = tone === 'info'

    const icon = (() => {
        if (passes) {
            return <CheckCircle className="h-5 w-5 shrink-0 text-green-500" />
        }
        if (isInfo) {
            return <AlertCircle className="h-5 w-5 shrink-0 text-neutral-400" />
        }
        return <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
    })()

    const badgeTone = (() => {
        if (passes) {
            return 'bg-green-50 text-green-700'
        }
        if (isInfo) {
            return 'bg-neutral-100 text-neutral-600'
        }
        return 'bg-red-50 text-red-700'
    })()

    return (
        <Card className={clsx(isOpen && 'ring-1 ring-neutral-200')}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen && !passes)}
                disabled={passes}
                className={clsx(
                    'flex w-full items-center gap-3 p-4 text-left',
                    !passes && 'cursor-pointer',
                )}>
                {icon}
                <span className="min-w-0 flex-1">
                    <span className="block text-md font-semibold text-dark">{title}</span>
                    <span className="mt-0.5 block text-sm text-neutral-400">{description}</span>
                </span>
                <span
                    className={clsx(
                        'shrink-0 rounded-full px-2.5 py-0.5 text-md font-semibold tabular-nums',
                        badgeTone,
                    )}>
                    {count}
                    {countSuffix && <span className="ml-1 text-sm font-normal">{countSuffix}</span>}
                </span>
                {!passes &&
                    (isOpen ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-neutral-400" />
                    ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-neutral-300" />
                    ))}
            </button>
            {isOpen && !passes && (
                <div className="border-t border-neutral-100 px-4 py-4">{children}</div>
            )}
        </Card>
    )
}

const InvoiceRowsTable = ({ rows }: { rows: AuditInvoiceRow[] }) => (
    <table className="w-full text-md">
        <thead>
            <tr className="text-left text-sm uppercase tracking-wide text-neutral-400">
                <th className="py-1.5 pr-3">Invoice</th>
                <th className="py-1.5 pr-3">Provider</th>
                <th className="py-1.5 pr-3">Country</th>
                <th className="py-1.5 pr-3 text-right">Amount</th>
                <th className="py-1.5">Reason / status</th>
            </tr>
        </thead>
        <tbody>
            {rows.map((row) => (
                <tr key={row.invoiceId} className="border-t border-neutral-100">
                    <td className="py-1.5 pr-3 tabular-nums">{row.invoiceNr}</td>
                    <td className="py-1.5 pr-3">
                        <ProviderLink id={row.serviceProviderId} name={row.businessName} />
                    </td>
                    <td className="py-1.5 pr-3">{row.countryCode}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                        {formatEuro(row.amountNetto)}
                    </td>
                    <td className="py-1.5">
                        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-sm font-medium text-neutral-600">
                            {(row.reason || row.transactionStatus || '—').replace(/_/g, ' ')}
                        </span>
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
)

const ChargesBucketTable = ({ bucket }: { bucket: AuditChargesBucket }) => (
    <table className="w-full text-md">
        <thead>
            <tr className="text-left text-sm uppercase tracking-wide text-neutral-400">
                <th className="py-1.5 pr-3">Provider</th>
                <th className="py-1.5 pr-3 text-right">Nr charges</th>
                <th className="py-1.5 text-right">Total amount</th>
            </tr>
        </thead>
        <tbody>
            {bucket.rows.map((row) => (
                <tr key={row.serviceProviderId} className="border-t border-neutral-100">
                    <td className="py-1.5 pr-3">
                        <ProviderLink id={row.serviceProviderId} name={row.businessName} />
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{row.nrCharges}</td>
                    <td className="py-1.5 text-right tabular-nums">
                        {formatEuro(row.totalAmount)}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
)

interface AuditTabProps {
    month: string
}

/**
 * Live per-month verification: reflects the current state of the data, so
 * audits from months ago account for corrections made since.
 */
const AuditTab = ({ month }: AuditTabProps) => {
    const { audit, isFetching, error, refresh, reconcileQueued, isReconciling, reconcileResults } =
        useBillingRunAudit(month, true)

    if (isFetching && !audit) {
        return <LoadingDots />
    }
    if (error) {
        return (
            <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-md text-red-700">
                <XCircle className="h-4 w-4 shrink-0" />
                {error}
                <button type="button" onClick={refresh} className="font-semibold underline">
                    retry
                </button>
            </div>
        )
    }
    if (!audit) {
        return null
    }

    const {
        uninvoicedCharges,
        missingTransactions,
        unmailedInvoices,
        failedTransactions,
        stuckQueuedTransactions,
    } = audit
    const uninvoicedCount =
        uninvoicedCharges.directDebit.count +
        uninvoicedCharges.manualTransfer.count +
        uninvoicedCharges.missingPaymentInfo.count
    const uninvoicedAmount =
        uninvoicedCharges.directDebit.totalAmount +
        uninvoicedCharges.manualTransfer.totalAmount +
        uninvoicedCharges.missingPaymentInfo.totalAmount
    // Failed transactions are informational (handled by the unpaid-invoices
    // follow-up), so they don't count towards the month verdict.
    const openIssues =
        uninvoicedCount +
        missingTransactions.count +
        unmailedInvoices.count +
        stuckQueuedTransactions.count
    const isClean = openIssues === 0

    return (
        <div className="grid gap-4">
            <div
                className={clsx(
                    'flex items-center gap-3 rounded-xl border px-4 py-3.5',
                    isClean ? 'border-green-100 bg-green-50' : 'border-amber-100 bg-amber-50',
                )}>
                {isClean ? (
                    <CheckCircle className="h-6 w-6 shrink-0 text-green-500" />
                ) : (
                    <AlertCircle className="h-6 w-6 shrink-0 text-amber-500" />
                )}
                <div className="min-w-0 flex-1">
                    <p
                        className={clsx(
                            'text-md font-semibold',
                            isClean ? 'text-green-700' : 'text-amber-700',
                        )}>
                        {isClean
                            ? `${month} looks complete`
                            : `${openIssues} item${openIssues === 1 ? '' : 's'} need${openIssues === 1 ? 's' : ''} attention for ${month}`}
                    </p>
                    <p className={clsx('text-sm', isClean ? 'text-green-600' : 'text-amber-600')}>
                        Live check on the current data — corrections made after a run are taken into
                        account.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isFetching}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-neutral-600 shadow-xs ring-1 ring-neutral-200 transition hover:bg-neutral-50">
                    <RefreshCw02 className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
                    Refresh
                </button>
            </div>

            <AuditSection
                title="Charges not invoiced"
                description="Open charges of this month without an invoice. Re-run the generate step; groups without payment info need manual follow-up first."
                count={uninvoicedCount}
                countSuffix={uninvoicedCount > 0 ? formatEuro(uninvoicedAmount) : undefined}>
                <div className="grid gap-4">
                    {uninvoicedCharges.directDebit.count > 0 && (
                        <div>
                            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-neutral-400">
                                Direct debit ·{' '}
                                {formatEuro(uninvoicedCharges.directDebit.totalAmount)}
                            </p>
                            <ChargesBucketTable bucket={uninvoicedCharges.directDebit} />
                        </div>
                    )}
                    {uninvoicedCharges.manualTransfer.count > 0 && (
                        <div>
                            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-neutral-400">
                                Manual transfer ·{' '}
                                {formatEuro(uninvoicedCharges.manualTransfer.totalAmount)}
                            </p>
                            <ChargesBucketTable bucket={uninvoicedCharges.manualTransfer} />
                        </div>
                    )}
                    {uninvoicedCharges.missingPaymentInfo.count > 0 && (
                        <div>
                            <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-red-600">
                                No payment info ·{' '}
                                {formatEuro(uninvoicedCharges.missingPaymentInfo.totalAmount)}
                            </p>
                            <ChargesBucketTable bucket={uninvoicedCharges.missingPaymentInfo} />
                        </div>
                    )}
                </div>
            </AuditSection>

            <AuditSection
                title="Direct-debit invoices without transaction"
                description="Should get a Mollie direct debit but have none — with the reason derived from the data. Fix the data, then re-run the transactions step."
                count={missingTransactions.count}>
                <InvoiceRowsTable rows={missingTransactions.rows} />
            </AuditSection>

            <AuditSection
                title="Invoices without invoice mail"
                description="Regular monthly invoices without an InvoiceMail event. Re-run the send-mails step to pick these up."
                count={unmailedInvoices.count}>
                <InvoiceRowsTable rows={unmailedInvoices.rows} />
            </AuditSection>

            <AuditSection
                title="Stuck queued transactions"
                description="Marked 'queued' hours ago but never got a Mollie transaction id — the worker died mid-item. Reconcile asks Mollie whether the payment exists and either adopts it or clears the invoice for the next run."
                count={stuckQueuedTransactions.count}>
                <div className="grid gap-3">
                    <InvoiceRowsTable rows={stuckQueuedTransactions.rows} />
                    <div>
                        <button
                            type="button"
                            onClick={reconcileQueued}
                            disabled={isReconciling}
                            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-md font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
                            <RefreshCw02
                                className={clsx('h-4 w-4', isReconciling && 'animate-spin')}
                            />
                            {isReconciling ? 'Reconciling…' : 'Reconcile via Mollie'}
                        </button>
                    </div>
                    {reconcileResults && (
                        <ul className="grid gap-1 text-sm text-neutral-600">
                            {reconcileResults.map((result) => (
                                <li key={result.invoiceId} className="flex items-center gap-1.5">
                                    {result.outcome === 'error' ? (
                                        <XCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
                                    ) : (
                                        <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                                    )}
                                    <span className="tabular-nums">{result.invoiceNr}</span>
                                    {result.outcome === 'adopted' &&
                                        `— adopted ${result.transactionId} (${result.transactionStatus})`}
                                    {result.outcome === 'cleared' &&
                                        '— no payment at Mollie, cleared for the next run'}
                                    {result.outcome === 'error' && `— ${result.message}`}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </AuditSection>

            <AuditSection
                title="Failed / charged-back / expired transactions"
                description="Did not (yet) result in payment. Follow-up runs via the unpaid-invoices flow — shown here for visibility, not counted as an open issue."
                count={failedTransactions.count}
                tone="info">
                <InvoiceRowsTable rows={failedTransactions.rows} />
            </AuditSection>
        </div>
    )
}

export default AuditTab
